import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { Chip, IconButton, Menu, MenuItem, TableCell, TableRow, Typography } from '@mui/material'
import Content from 'components/content/content'
import CONSTANT from 'modules/constant'
import type { PartialPopUpBannerProps } from 'modules/partial'
import Icon from 'components/icon'
import GLOBAL from 'modules/global'
import { useDeletePopUpBannerMutation, useGetPopUpBannerListMutation } from 'store/popUpBanner'
import MenuModalItem from 'components/menuItem/menuItem'
import GlobalStyle from 'modules/styles'
import type { ErrorProps } from 'modules/types'
import Notification from 'components/notification'
import Detail from './detail/detail'
import Create from './create/create'
import Edit from './edit/edit'

const PopUpBannerList = () => {
  //   const navigate = useNavigate()
  const [getPopUpBanner, popUpBanner] = useGetPopUpBannerListMutation()
  const [deletePopUpBannerEvent, deletePopUpBanner] = useDeletePopUpBannerMutation()

  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [menu, setMenu] = useState<null | number>(null)

  const [drawerDetail, setDrawerDetail] = useState<boolean>(false)
  const [dialogUpdate, setDialogUpdate] = useState<boolean>(false)
  const [dialogCreate, setDialogCreate] = useState<boolean>(false)

  const [payload, setPayload] = useState<PartialPopUpBannerProps>({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    field: ['campaignName', 'bannerType', 'segmentationType', 'status'].join(','),
    search: { value: '' },
    campaignName: '',
    periodStartString: '',
    periodEndString: '',
    bannerType: '',
    segmentationType: '',
    status: '',
    viewCount: 0,
    clickCount: 0,
    dismissCount: 0
  })

  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })

  const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: { value } })

  const onAction = (event: React.MouseEvent<HTMLElement>, index: null | number) => {
    setAnchor(event.currentTarget)
    setMenu(index)
  }

  const onResync = () => {
    const formData = new FormData()

    formData.append('start', payload.start.toString())
    formData.append('length', payload.length.toString())
    formData.append('field', payload.field)

    if (!isEmpty(payload.search.value)) formData.append('search[value]', payload.search.value)
    if (!isEmpty(payload.campaignName)) formData.append('name', payload.campaignName)
    if (!isEmpty(payload.periodStartString)) formData.append('category', payload.periodStartString)
    if (!isEmpty(payload.periodEndString)) formData.append('value', payload.periodEndString)
    if (!isEmpty(payload.bannerType)) formData.append('description', payload.bannerType)
    if (!isEmpty(payload.segmentationType)) formData.append('description', payload.segmentationType)
    if (!isEmpty(payload.status)) formData.append('description', payload.status)

    getPopUpBanner(formData)
  }

  const onRemoveAnchor = async (callback: CallableFunction) => {
    setAnchor(null)
    callback && callback(callback)
  }

  const numbers = GLOBAL.tableNumber(payload.start)
  const totalPage = popUpBanner.data && GLOBAL.tableTotalPage(popUpBanner.data.recordsTotal)
  const tableHeadTitles = [
    'Pop Up Banner Campaign',
    'Period Start',
    'Period End',
    'Banner Type',
    'Segmentation Type',
    'Status',
    'Created By',
    'View Count',
    'Click Count',
    'Dismiss Count'
  ]
  const openAnchor = Boolean(anchor)

  const onCreate = () => setDialogCreate(true)

  useEffect(() => {
    onResync()
  }, [payload])

  return (
    <>
      <Content
        title='Pop Up Banner List'
        tableHeadTitles={tableHeadTitles}
        uploadTitle='Create New Pop Up Banner'
        uploadPrefixIcon='Add'
        additionalPrefixIcon='Create'
        onSearch={onSearch}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        onUpload={onCreate}
        totalRecords={popUpBanner.data && popUpBanner.data.recordsTotal}
        page={payload.start + 1}
        isLoading={popUpBanner.isLoading}
      >
        {popUpBanner.isSuccess &&
          popUpBanner.data &&
          popUpBanner.data.data.map((table, index) => (
            <TableRow key={index}>
              <TableCell>{numbers[index] + 1}</TableCell>
              <TableCell>
                <IconButton
                  id={`button-${index}-${table.id}`}
                  aria-haspopup='true'
                  aria-controls={openAnchor ? `menu-${index}-${table.id}` : undefined}
                  aria-expanded={openAnchor ? 'true' : undefined}
                  onClick={(event) => onAction(event, index)}
                >
                  <Icon icon='MoreHoriz' />
                </IconButton>
                <Menu
                  id={`menu-${index}-${table.id}`}
                  aria-labelledby={`button-${index}-${table.id}`}
                  anchorEl={anchor}
                  open={openAnchor && menu == index}
                  onClose={() => onRemoveAnchor(() => setMenu(null))}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                >
                  <MenuItem onClick={() => onRemoveAnchor(() => setDrawerDetail(true))}>
                    View
                  </MenuItem>
                  <MenuItem
                    onClick={() => onRemoveAnchor(() => setDialogUpdate(true))}
                    disabled={false}
                  >
                    Edit
                  </MenuItem>
                  <MenuModalItem
                    title='Delete'
                    loading={deletePopUpBanner.isLoading}
                    onClick={() =>
                      onRemoveAnchor(() =>
                        deletePopUpBannerEvent({
                          id: table.id
                        })
                      )
                    }
                    // disabled={isDelete(table.status, table.airingTime)}
                  >
                    <Typography {...GlobalStyle.ErrorTypography}>Delete</Typography>
                  </MenuModalItem>
                </Menu>
                <Detail
                  id={table.id}
                  open={drawerDetail && menu == index}
                  onClose={() => setDrawerDetail(false)}
                />
                <Edit
                  id={table.id}
                  title={table.campaignName}
                  open={dialogUpdate && menu == index}
                  onClose={() => setDialogUpdate(false)}
                />
              </TableCell>
              <TableCell>{table.campaignName || '-'}</TableCell>
              <TableCell>{table.periodStartString || '-'}</TableCell>
              <TableCell>{table.periodEndString || '-'}</TableCell>
              <TableCell>
                {table.bannerType === 'textOnly'
                  ? 'Text'
                  : table.bannerType === 'visual'
                  ? 'Key Visual'
                  : table.bannerType === 'visualAndText'
                  ? 'Key Visual + Text'
                  : '-'}
              </TableCell>
              <TableCell>
                {table.segmentationType === 'carModel'
                  ? 'Car Model'
                  : table.segmentationType === 'memberTier'
                  ? 'Member Tier'
                  : '-'}
              </TableCell>
              <TableCell>
                {table.status === 'Active' ? (
                  <Chip label={table.status} color='primary' />
                ) : table.status === 'InActive' ? (
                  <Chip label={table.status} color='error' />
                ) : (
                  table.status || '-'
                )}
              </TableCell>
              <TableCell>{table.createdByName}</TableCell>

              <TableCell>{table.viewCount || '-'}</TableCell>
              <TableCell>{table.clickCount || '-'}</TableCell>
              <TableCell>{table.dismissCount || '-'}</TableCell>
            </TableRow>
          ))}
      </Content>
      <Notification
        open={!deletePopUpBanner.isLoading && !deletePopUpBanner.isUninitialized}
        onClose={() => (deletePopUpBanner.isError ? deletePopUpBanner.reset() : location.reload())}
        isError={Boolean(deletePopUpBanner.error) && deletePopUpBanner.isError}
        message={GLOBAL.returnExceptionMessage(
          deletePopUpBanner.isError,
          deletePopUpBanner.error as ErrorProps
        )}
      />

      <Create open={dialogCreate} onClose={() => setDialogCreate(false)} />
    </>
  )
}

export default PopUpBannerList
